import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const POLL_INTERVAL_MS = 2000;
const SAMPLE_DISPLAY_CAP = 50;

// Same job-polling shape as RevolutCounterpartyBackfillSection.jsx, kept
// as its own copy rather than a shared import -- matches this codebase's
// existing convention of one hook per feature (see that file's comment).
function useJobPolling(statusFn, { onCompleted, onFailed, onProgress }) {
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current);
      elapsedRef.current = null;
    }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((id) => {
    stop();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await statusFn(id);
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error, data.progress || null);
        } else if (data.status === 'running') {
          onProgress(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, statusFn, onCompleted, onFailed, onProgress]);

  return { polling, elapsed, start };
}

function ProgressBadge({ progress }) {
  if (!progress) return null;
  const label = progress.phase === 'applying' ? 'APPLYING' : 'CLASSIFYING';
  const total = progress.total_scanned;
  return (
    <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
      <Loader2 className="h-3 w-3 animate-spin mr-1 inline" />
      {label} -- {fmtInt(progress.scanned)} of {fmtInt(total)} scanned
      {progress.phase === 'applying'
        ? ` · ${fmtInt(progress.written)} written`
        : ` · ${fmtInt(progress.candidates_found)} candidates found`}
    </Badge>
  );
}

function PatternTagCounts({ tagCounts }) {
  if (!tagCounts || Object.keys(tagCounts).length === 0) return null;
  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-muted/30">
            <th className="px-2 py-1 text-left">Outcome (pattern tag)</th>
            <th className="px-2 py-1 text-right">Count</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(tagCounts).map(([tag, count]) => (
            <tr key={tag} className="border-b last:border-0">
              <td className="px-2 py-1 font-mono">
                {tag}
                {tag === 'no_pattern' || tag === 'empty' ? (
                  <span className="text-muted-foreground"> (left null)</span>
                ) : (
                  <span className="text-green-600"> (parsed)</span>
                )}
              </td>
              <td className="px-2 py-1 text-right">{fmtInt(count)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreditorFieldCensusSection() {
  const [loading, setLoading] = useState(false);
  const [census, setCensus] = useState(null);
  const [error, setError] = useState(null);

  const runCensus = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.ebCreditorFieldCensus();
      setCensus(data);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-t pt-3 space-y-3" data-testid="eb-creditor-census-section">
      <div>
        <p className="text-sm font-medium">Creditor Field Census</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Read-only. Reports what the raw creditor/debtor keys and credit_debit_indicator actually
          contain across stored Enable Banking payloads -- fill rate, value shape, and verbatim
          samples from rows currently tagged no_pattern -- before trusting any backfill numbers.
        </p>
      </div>

      <Button onClick={runCensus} disabled={loading} size="sm" data-testid="eb-creditor-census-run">
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
        Run Creditor Field Census
      </Button>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {census && (
        <div className="space-y-3" data-testid="eb-creditor-census-result">
          <p className="text-[10px] text-muted-foreground">
            {fmtInt(census.total_enablebanking_transactions)} Enable Banking transactions &middot;
            generated {census.generated_at}
          </p>

          {Object.entries(census.fields || {}).map(([key, field]) => (
            <div key={key} className="border rounded-lg p-2 space-y-2">
              <p className="text-xs font-mono font-medium">
                eb_raw_fields.{key}
                <span className="text-muted-foreground font-sans ml-2">
                  {fmtInt(field.present_non_null)} / {fmtInt(census.total_enablebanking_transactions)} present
                  {field.fill_rate != null ? ` (${(field.fill_rate * 100).toFixed(1)}%)` : ''}
                </span>
              </p>
              <p className="text-[11px] text-muted-foreground">
                Shape(s) observed: {field.value_shapes_observed.length > 0 ? field.value_shapes_observed.join(', ') : 'none (key never present)'}
                {field.shape_sample_size < field.present_non_null && (
                  <span> (from a sample of {fmtInt(field.shape_sample_size)} of {fmtInt(field.present_non_null)} present rows -- shape/sub-key stats below are not exhaustive)</span>
                )}
              </p>

              {Object.keys(field.sub_keys || {}).length > 0 && (
                <div className="overflow-x-auto border rounded">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="px-2 py-1 text-left">Sub-key</th>
                        <th className="px-2 py-1 text-right">Non-null</th>
                        <th className="px-2 py-1 text-right">Fill rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(field.sub_keys).map(([sk, stats]) => (
                        <tr key={sk} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{key}.{sk}</td>
                          <td className="px-2 py-1 text-right">{fmtInt(stats.non_null_count)}</td>
                          <td className="px-2 py-1 text-right">
                            {stats.fill_rate != null ? `${(stats.fill_rate * 100).toFixed(1)}%` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {field.no_pattern_verbatim_samples && field.no_pattern_verbatim_samples.length > 0 ? (
                <details>
                  <summary className="text-xs font-medium cursor-pointer">
                    Verbatim samples from no_pattern rows ({field.no_pattern_verbatim_sample_count})
                  </summary>
                  <div className="mt-2 overflow-x-auto border rounded max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {field.no_pattern_verbatim_samples.map((s, i) => (
                          <tr key={s.transaction_id || i} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono whitespace-nowrap">{s.transaction_id}</td>
                            <td className="px-2 py-1 text-muted-foreground">{s.description}</td>
                            <td className="px-2 py-1 font-mono text-[10px]">{JSON.stringify(s.value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              ) : (
                <p className="text-[10px] text-muted-foreground">No no_pattern rows carry this key.</p>
              )}
            </div>
          ))}

          <div className="border rounded-lg p-2">
            <p className="text-xs font-medium mb-1">credit_debit_indicator (distinct values)</p>
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="px-2 py-1 text-left">Value</th>
                    <th className="px-2 py-1 text-right">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(census.credit_debit_indicator?.by_value || {}).map(([val, count]) => (
                    <tr key={val} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono">{val}</td>
                      <td className="px-2 py-1 text-right">{fmtInt(count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function EbCounterpartyBackfillSection() {
  const [dispatching, setDispatching] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);

  const previewPoll = useJobPolling(diagnosticsAPI.ebCounterpartyBackfillJobStatus, {
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      setPreview(result);
      toast.success('EB counterparty preview complete');
    }, []),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      toast.error(error ?? 'preview failed');
    }, []),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling(diagnosticsAPI.ebCounterpartyBackfillJobStatus, {
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      setApplyResult(result);
      setPreview(null);
      toast.success(`EB backfill applied: ${result?.written ?? 0} written, ${result?.skipped_conflict ?? 0} skipped`);
    }, []),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      toast.error(error ?? 'apply failed');
    }, []),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.ebCounterpartyBackfillJobLatest('preview');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setPreview(job.result);
        } else if (job.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.ebCounterpartyBackfillJobLatest('apply');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setApplyResult(job.result);
        } else if (job.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runPreview = async () => {
    setDispatching(true);
    setPreview(null);
    setPreviewProgress(null);
    setPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.ebCounterpartyBackfillPreviewStart();
      previewPoll.start(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const runApply = async () => {
    const count = preview ? preview.total_candidates : null;
    if (!window.confirm(
      count != null
        ? `This will backfill counterparty on ${count} Enable Banking transaction(s) by re-parsing the stored description. Continue?`
        : 'This will backfill counterparty on any Enable Banking transactions the description parser resolves. Continue?'
    )) return;

    setDispatching(true);
    setApplyResult(null);
    setApplyProgress(null);
    setApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.ebCounterpartyBackfillApplyStart();
      applyPoll.start(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const previewPolling = previewPoll.polling;
  const applyPolling = applyPoll.polling;
  const busy = dispatching || previewPolling || applyPolling;

  return (
    <div className="border rounded-lg p-3 space-y-3" data-testid="eb-counterparty-backfill-section">
      <div>
        <p className="text-sm font-medium">Enable Banking Counterparty Backfill (historical)</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Re-parses the already-stored description for historical Enable Banking rows still carrying
          a null/empty counterparty -- no live refetch needed. Preview re-parses without writing;
          Apply re-checks fresh and writes, always stamping a pattern tag for coverage visibility even
          when no counterparty is recovered. Uses the same background-job infrastructure as the
          Revolut backfill above for a consistent UX between the two.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={runPreview} disabled={busy} size="sm" data-testid="eb-cp-backfill-preview">
          {previewPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
          Preview
        </Button>

        <Button
          onClick={runApply}
          disabled={busy}
          size="sm"
          variant={preview && preview.total_candidates > 0 ? 'default' : 'outline'}
          data-testid="eb-cp-backfill-apply"
        >
          {applyPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          {preview ? `Apply (${fmtInt(preview.total_candidates)} candidates)` : 'Apply'}
        </Button>

        {previewPolling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            preview {previewPoll.elapsed}s
          </span>
        )}
        {applyPolling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            apply {applyPoll.elapsed}s
          </span>
        )}
      </div>

      {previewPolling && previewProgress && (
        <div data-testid="eb-cp-backfill-preview-progress">
          <ProgressBadge progress={previewProgress} />
        </div>
      )}

      {applyPolling && applyProgress && (
        <div data-testid="eb-cp-backfill-apply-progress">
          <ProgressBadge progress={applyProgress} />
        </div>
      )}

      {preview && (
        <div className="space-y-3">
          <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
            PREVIEW (read-only) -- {fmtInt(preview.total_scanned)} scanned, {fmtInt(preview.total_candidates)} candidates
          </Badge>
          <PatternTagCounts tagCounts={preview.pattern_tag_counts} />
          {preview.proposed_corrections && preview.proposed_corrections.length > 0 && (
            <details>
              <summary className="text-sm font-medium cursor-pointer">
                Proposed corrections ({preview.proposed_corrections.length}
                {preview.proposed_corrections.length > SAMPLE_DISPLAY_CAP ? `, showing first ${SAMPLE_DISPLAY_CAP}` : ''})
              </summary>
              <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {preview.proposed_corrections.slice(0, SAMPLE_DISPLAY_CAP).map((c, i) => (
                      <tr key={c.transaction_id || i} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono whitespace-nowrap">{c.transaction_id}</td>
                        <td className="px-2 py-1">{c.new_counterparty}</td>
                        <td className="px-2 py-1 font-mono text-[10px]">{c.pattern_tag}</td>
                        <td className="px-2 py-1 text-muted-foreground">{c.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
          <p className="text-[10px] text-muted-foreground">Generated: {preview.generated_at}</p>
        </div>
      )}

      {previewFailed && (
        <div className="space-y-1">
          <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">PREVIEW FAILED</Badge>
          <p className="text-xs text-red-600">{previewFailed}</p>
        </div>
      )}

      {applyFailed && (
        <div className="space-y-1">
          <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">APPLY FAILED</Badge>
          <p className="text-xs text-red-600">{applyFailed}</p>
        </div>
      )}

      {applyResult && (
        <div className="space-y-3">
          <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
            APPLIED -- {fmtInt(applyResult.written)} written, {fmtInt(applyResult.skipped_conflict)} skipped (conflict)
          </Badge>
          <PatternTagCounts tagCounts={applyResult.pattern_tag_counts} />
          {applyResult.written_samples && applyResult.written_samples.length > 0 && (
            <details>
              <summary className="text-sm font-medium cursor-pointer">
                Written samples ({applyResult.written_samples.length})
              </summary>
              <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {applyResult.written_samples.map((s, i) => (
                      <tr key={s.transaction_id || i} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{s.transaction_id}</td>
                        <td className="px-2 py-1">{s.new_counterparty}</td>
                        <td className="px-2 py-1 font-mono text-[10px]">{s.pattern_tag}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
          <p className="text-[10px] text-muted-foreground">Run: {applyResult.run_id} &middot; Generated: {applyResult.generated_at}</p>
        </div>
      )}

      <CreditorFieldCensusSection />
    </div>
  );
}
